import { useState } from 'react';
import { Layers } from 'lucide-react';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import type { Tanda } from './tiposDelProcesoSri';

const ESTADOS: Record<Tanda['estado'], { etiqueta: string; clase: string }> = {
  en_curso: { etiqueta: 'En curso', clase: 'border-marca-200 bg-marca-50 text-marca-700' },
  terminada: { etiqueta: 'Terminada', clase: 'border-marca-200 bg-marca-50 text-marca-700' },
  detenida: { etiqueta: 'Detenida', clase: 'border-armazon-200 bg-armazon-50 text-armazon-600' },
  interrumpida: { etiqueta: 'Interrumpida', clase: 'border-amber-200 bg-amber-50 text-amber-700' },
};

export function TablaDeTandas({ tandas }: { tandas: Tanda[] }) {
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(10);

  const totalPaginas = Math.max(1, Math.ceil(tandas.length / tamanio));
  const actual = Math.min(pagina, totalPaginas - 1);

  const datos = {
    contenido: tandas.slice(actual * tamanio, actual * tamanio + tamanio),
    totalElementos: tandas.length,
    pagina: actual,
    tamanio,
    totalPaginas,
  };

  const columnas: Columna<Tanda>[] = [
    {
      clave: 'id',
      encabezado: 'Nº',
      contenido: (t) => (
        <span className="font-mono text-xs font-bold text-armazon-700">#{t.id}</span>
      ),
    },
    {
      clave: 'iniciadoEn',
      encabezado: 'Lanzada',
      contenido: (t) => (
        <span className="block">
          <span className="block whitespace-nowrap text-[11px] text-armazon-600">
            {new Date(t.iniciadoEn).toLocaleString('es-EC', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </span>
          {t.iniciadoPor && (
            <span className="block truncate text-[10px] text-armazon-400">{t.iniciadoPor}</span>
          )}
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      alinear: 'centro',
      contenido: (t) => (
        <span
          className={`inline-block rounded-lg border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${ESTADOS[t.estado].clase}`}
        >
          {ESTADOS[t.estado].etiqueta}
        </span>
      ),
    },
    {
      clave: 'avance',
      encabezado: 'Procesadas',
      alinear: 'derecha',
      contenido: (t) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-armazon-600">
          {t.procesadas.toLocaleString('es-EC')}
          <span className="text-armazon-400"> / {t.total.toLocaleString('es-EC')}</span>
        </span>
      ),
    },
    {
      clave: 'emitidas',
      encabezado: 'Autorizadas',
      alinear: 'derecha',
      contenido: (t) => (
        <span className="text-xs font-semibold tabular-nums text-marca-700">
          {t.emitidas.toLocaleString('es-EC')}
        </span>
      ),
    },
    {
      clave: 'rechazadas',
      encabezado: 'Rechazadas',
      alinear: 'derecha',
      contenido: (t) => (
        <span
          className={`text-xs tabular-nums ${t.rechazadas > 0 ? 'font-semibold text-red-700' : 'text-armazon-300'}`}
        >
          {t.rechazadas.toLocaleString('es-EC')}
        </span>
      ),
    },
    {
      clave: 'errores',
      encabezado: 'Sin respuesta',
      alinear: 'derecha',
      contenido: (t) => (
        <span
          className={`text-xs tabular-nums ${t.errores > 0 ? 'font-semibold text-amber-700' : 'text-armazon-300'}`}
        >
          {t.errores.toLocaleString('es-EC')}
        </span>
      ),
    },
    {
      clave: 'duracion',
      encabezado: 'Duración',
      alinear: 'derecha',
      contenido: (t) => (
        <span className="whitespace-nowrap text-[11px] tabular-nums text-armazon-500">
          {duracion(t)}
        </span>
      ),
    },
    {
      clave: 'ritmo',
      encabezado: 'Por segundo',
      alinear: 'derecha',
      contenido: (t) => (
        <span className="text-[11px] tabular-nums text-armazon-500">
          {t.porSegundo > 0 ? t.porSegundo.toFixed(2) : '—'}
        </span>
      ),
    },
    {
      clave: 'exito',
      encabezado: 'Éxito',
      alinear: 'derecha',
      contenido: (t) => {
        const exito = t.procesadas > 0 ? (t.emitidas / t.procesadas) * 100 : 0;
        return (
          <span
            className={`text-xs font-semibold tabular-nums ${
              t.procesadas === 0
                ? 'text-armazon-300'
                : exito >= 95
                  ? 'text-marca-700'
                  : exito >= 80
                    ? 'text-amber-700'
                    : 'text-red-700'
            }`}
          >
            {t.procesadas === 0 ? '—' : `${exito.toFixed(1)} %`}
          </span>
        );
      },
    },
  ];

  return (
    <Tabla
      datos={datos}
      cargando={false}
      columnas={columnas}
      tamanio={tamanio}
      alCambiarPagina={setPagina}
      alCambiarTamanio={(nuevo) => {
        setTamanio(nuevo);
        setPagina(0);
      }}
      iconoVacio={Layers}
      mensajeVacio="Todavía no se ha lanzado ninguna emisión"
      entidad="EMISIONES"
    />
  );
}

function duracion(t: Tanda): string {
  const hasta = t.terminadoEn ? new Date(t.terminadoEn) : new Date();
  const segundos = Math.max(
    0,
    Math.floor((hasta.getTime() - new Date(t.iniciadoEn).getTime()) / 1000),
  );

  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);

  if (horas > 0) {
    return `${horas} h ${minutos} min`;
  }
  return minutos > 0 ? `${minutos} min` : `${segundos} s`;
}
